import React, { useCallback, useEffect, useState } from 'react';
import { Button, Clickable, Input, Label, LoadingBalls, Stack, Text } from '@moda/om';
import ArrowDownFilledIcon from '@moda/icons/arrow-down-filled-20';
import ArrowUpFilledIcon from '@moda/icons/arrow-up-filled-20';
import { formatPrice, useForm, FulfillmentTypes } from '@moda/portal-stanchions';
import { useQuery } from '@apollo/client';
import { useCountry } from '../../../CountrySelector/useCountry';
import { useUser } from '../../../../hooks/useUser';
import { tc } from '../../../../lib/trackingContext';
import { usePageType } from '../../../../hooks/usePageType';
import { useAddressesQuery } from '../../../../hooks/useAddress';
import { useSmartyStreetsState } from '../../../../hooks/useSmartyStreetsState';
import { SizeInventoryFragment } from '../../../../generated/types';
import { GET_DELIVERY_ESTIMATE_QUERY } from './deliveryEstimate.rest';
import { useCountryCodeAndCurrency } from './useCountryCodeAndCurrency';

import './ProductShippingEstimate.scss';

const validations = {
  zipCode: {
    pattern: {
      message: 'Please enter a valid zip code.',
      value: /^\b\d{5}(-\d{4})?\b$/
    },
    required: {
      message: 'Please enter a destination zip code.',
      value: true
    }
  },
  destinationCountry: {
    pattern: {
      message: 'Please enter a valid country.',
      value: /^[a-zA-Z,.\s]+$/
    },
    required: {
      message: 'Please enter a destination country.',
      value: true
    }
  }
};

interface Props {
  variantId: string;
  fulfilmentType: number | null;
  size: SizeInventoryFragment | null;
}

export const ProductShippingEstimate: React.FC<Props> = ({ variantId, size, fulfilmentType }) => {
  const { user } = useUser();
  const { addresses, loading: loadingAddresses } = useAddressesQuery();
  const { country } = useCountry();
  const [expanded, setExpanded] = useState(false);
  const [showDefaultLocation, setShowDefaultLocation] = useState<boolean>();
  const [zipCode, setZipCode] = useState<string | undefined>();
  const [destinationCountry, setDestinationCountry] = useState<string | undefined>();
  const pagetype = usePageType();

  const domesticUser =
    user.isLoggedIn &&
    country.alpha3Code === 'USA' &&
    addresses.length > 0 &&
    addresses[0].attributes.countryCode === 'US';
  const internationalUser = country.alpha3Code !== 'USA';
  const mixedUser =
    user.isLoggedIn &&
    country.alpha3Code === 'USA' &&
    addresses.length > 0 &&
    addresses[0].attributes.countryCode !== 'US';
  const zipCodeFromUserAddresses = addresses && addresses[0]?.attributes.postalCode;
  const defaultCountry = country.name;

  const {
    loading: loadingSmartyStreetsState,
    isInvalid: invalidZipCode,
    abbreviation,
    name
  } = useSmartyStreetsState(zipCode || zipCodeFromUserAddresses);

  const { updatedCountryCode, updatedCurrency } = useCountryCodeAndCurrency(
    destinationCountry ? destinationCountry : defaultCountry
  );

  const { data, loading } = useQuery(GET_DELIVERY_ESTIMATE_QUERY, {
    variables: {
      variantId,
      zipCode,
      countryCode: updatedCountryCode,
      currency: updatedCurrency,
      stateCode: abbreviation
    },
    skip: loadingAddresses || loadingSmartyStreetsState
  });

  const toggleExpand = useCallback(() => {
    setExpanded(expanded => !expanded);
  }, []);

  useEffect(() => {
    if (domesticUser) {
      setShowDefaultLocation(true);
      setZipCode(zipCodeFromUserAddresses);
      setExpanded(false);
    } else if (!user.isLoggedIn && country.alpha3Code === 'USA') {
      setShowDefaultLocation(false);
      setExpanded(true);
    } else if (internationalUser || mixedUser) {
      setShowDefaultLocation(true);
      setExpanded(true);
    }
  }, [
    domesticUser,
    internationalUser,
    mixedUser,
    user.isLoggedIn,
    country.alpha3Code,
    zipCodeFromUserAddresses
  ]);

  const onSubmit = ({
    zipCode,
    destinationCountry
  }: {
    zipCode: string;
    destinationCountry: string;
  }) => {
    setZipCode(zipCode);
    setDestinationCountry(destinationCountry);
    setExpanded(false);
  };

  const { handleSubmit, errors: fieldErrors } = useForm({ onSubmit, validations });

  const invalidCountry = data === undefined && internationalUser;

  const trackZipCodeEntered = useCallback(() => {
    tc.track('Delivery Estimated with Zip Code', {
      addToTrackingEvent: { pagetype }
    });
  }, [pagetype]);

  const trackCountryEntered = useCallback(() => {
    tc.track('Delivery Estimated with Country', {
      addToTrackingEvent: { pagetype }
    });
  }, [pagetype]);

  const variantNotDropShipOrMirakl =
    fulfilmentType !== FulfillmentTypes.DropShip &&
    fulfilmentType !== FulfillmentTypes.MiraklEconcession &&
    fulfilmentType !== FulfillmentTypes.MiraklDropship;
  let skuIsOnHand, skuIsAvailable;

  if (size !== null) {
    const { onHand: skuOnHand, available: skuAvailability } = size;
    skuIsOnHand = skuOnHand !== 0;
    skuIsAvailable = skuAvailability !== 0;
  }

  return (
    <>
      {loading && (
        <div className="ProductShippingEstimate__overlay">
          <LoadingBalls className="ProductShippingEstimate__loading" />
        </div>
      )}
      <Stack space={1} className="ProductShippingEstimate">
        {data && (
          <Stack space={0}>
            <Text>
              {formatPrice(data.deliveryEstimate.currency, data.deliveryEstimate.charges)}{' '}
              {data.deliveryEstimate.name}
            </Text>
            {variantNotDropShipOrMirakl && skuIsOnHand && skuIsAvailable && (
              <Text>Estimated Delivery: {data.deliveryEstimate.arrivalDate}</Text>
            )}
          </Stack>
        )}

        {(showDefaultLocation === true || data) && (
          <Stack space={3} direction="horizontal" alignItems="center">
            <Stack space={1} direction="horizontal">
              <Text>Ship to:</Text>
              <Text treatment="bold1">{zipCode || destinationCountry || defaultCountry}</Text>
              {abbreviation &&
                abbreviation !== 'invalid' &&
                zipCode &&
                country.alpha3Code === 'USA' && <Text treatment="bold1"> — {name}</Text>}
            </Stack>
            <Clickable className="ProductShippingEstimate__action" onClick={() => toggleExpand()}>
              <Stack space={0} direction="horizontal" alignItems="center">
                <Text treatment="body1" className="ProductShippingEstimate__action-text">
                  Change
                </Text>
                {expanded && <ArrowUpFilledIcon />}
                {!expanded && <ArrowDownFilledIcon />}
              </Stack>
            </Clickable>
            {invalidZipCode && <Text color="code-red">Invalid zip code</Text>}
            {invalidCountry && <Text color="code-red">Invalid country</Text>}
          </Stack>
        )}

        <div className="ProductShippingEstimate__content">
          {expanded && (
            <form className="ProductShippingEstimate__form" onSubmit={handleSubmit}>
              {country.alpha3Code === 'USA' && (
                <Stack space={1}>
                  <Label>Enter a Zip/Postal Code for a delivery estimate</Label>
                  <Stack space={2} direction="horizontal" alignItems="center">
                    <Input type="text" placeholder="Zip Code" name="zipCode" />
                    <Button type="submit" onClick={trackZipCodeEntered}>
                      UPDATE
                    </Button>
                  </Stack>
                  {fieldErrors.zipCode && (
                    <Text color="code-red">{fieldErrors.zipCode.message}</Text>
                  )}
                </Stack>
              )}
              {country.alpha3Code !== 'USA' && (
                <Stack space={1}>
                  <Label>Enter country for a delivery estimate</Label>
                  <Stack space={2} direction="horizontal" alignItems="center">
                    <Input type="text" placeholder="Country" name="destinationCountry" />
                    <Button type="submit" onClick={trackCountryEntered}>
                      UPDATE
                    </Button>
                  </Stack>
                  {fieldErrors.destinationCountry && (
                    <Text color="code-red">{fieldErrors.destinationCountry.message}</Text>
                  )}
                </Stack>
              )}
            </form>
          )}
        </div>
      </Stack>
    </>
  );
};
